/**
 * L'accueil du module Utilisateurs : deux tuiles.
 *
 * Demandé par Éclosion le 27/09/2026 : le module n'est pas séparé entre les
 * espaces, mais « quand on s'y connecte il y a deux tuiles, une données de
 * l'utilisateur (pour changer son adresse par exemple) et une données
 * financières de l'utilisateur, qui nous permet rapidement via une recherche
 * de voir les montants payés, les montants dus, la provision, et le détail
 * des factures et NC ».
 *
 * Chaque tuile porte sa recherche : taper un nom et valider ouvre la liste
 * correspondante déjà filtrée (`?q=`). Les deux listes sont les écrans
 * existants ; cette page n'en recalcule rien.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Search, UserCog, Wallet } from 'lucide-react';
import { avecRecherche, CHEMINS } from '../../lib/navigation/chemins';

export default function AdminUtilisateursPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Utilisateurs</h1>
        <p className="text-gray-600">Les comptes des familles et des organisations.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Tuile
          icone={<UserCog className="h-6 w-6" />}
          titre="Données de l’utilisateur"
          detail="Identité, coordonnées, adresse, type de compte : pour retrouver un compte et corriger une adresse, par exemple."
          liste={CHEMINS.utilisateurs.donnees}
          libelleListe="Tous les comptes"
          ton="gris"
        />
        <Tuile
          icone={<Wallet className="h-6 w-6" />}
          titre="Données financières de l’utilisateur"
          detail="Montants facturés, payés, dus, provision — et, pour chaque famille, le détail de ses factures et notes de crédit."
          liste={CHEMINS.utilisateurs.finances}
          libelleListe="Tous les comptes des familles"
          ton="indigo"
        />
      </div>
    </div>
  );
}

function Tuile({
  icone, titre, detail, liste, libelleListe, ton,
}: {
  icone: ReactNode;
  titre: string;
  detail: string;
  liste: string;
  libelleListe: string;
  ton: 'gris' | 'indigo';
}) {
  const navigate = useNavigate();
  const [saisie, setSaisie] = useState('');

  const chercher = (e: FormEvent) => {
    e.preventDefault();
    navigate(avecRecherche(liste, saisie));
  };

  return (
    <section
      className={
        ton === 'indigo'
          ? 'flex flex-col rounded-xl border border-indigo-300 bg-indigo-50 p-5'
          : 'flex flex-col rounded-xl border border-gray-200 bg-white p-5'
      }
    >
      <h2 className={`flex items-center gap-2 text-lg font-semibold ${ton === 'indigo' ? 'text-indigo-900' : 'text-gray-900'}`}>
        {icone}
        {titre}
      </h2>
      <p className="mt-1 text-sm text-gray-600">{detail}</p>

      <form onSubmit={chercher} className="mt-4 flex gap-2" role="search">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            placeholder="Nom, prénom, e-mail, organisation"
            aria-label={`Chercher — ${titre}`}
            className="form-input w-full pl-9"
          />
        </div>
        <button type="submit" className="btn-primary shrink-0">Chercher</button>
      </form>

      <Link
        to={liste}
        className="mt-3 inline-flex items-center gap-1 self-start text-sm font-medium text-primary-700 hover:text-primary-900"
      >
        {libelleListe} <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  );
}
